import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { languages } from '@/lib/i18n';
import { buildUrlForLocaleSwitch, getExplicitLocaleFromPathname } from '@/lib/intl-seo';

export const LanguageSelector = ({ variant = 'default', compact = false }) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const changeLanguage = (code) => {
    localStorage.setItem('userSelectedLanguage', code);
    localStorage.setItem('i18nextLng', code);
    const next = buildUrlForLocaleSwitch(code, location.pathname, location.search, location.hash);
    const current = `${location.pathname}${location.search}${location.hash}`;

    if (next === current) {
      i18n.changeLanguage(code);
      return;
    }

    navigate(next);
    // İngilizce kök URL'lerde (/products) önek yok — Layout senkronize etmez.
    const nextPathOnly = next.split('?')[0].split('#')[0];
    if (!getExplicitLocaleFromPathname(nextPathOnly)) {
      i18n.changeLanguage(code);
    }
  };

  React.useEffect(() => {
    // Set document direction for RTL languages
    const lang = languages.find(l => l.code === i18n.language);
    document.documentElement.dir = lang?.dir || 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  // Compact mode for mobile header
  if (compact || variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 px-2 gap-1 rounded-full" data-testid="language-selector-mobile">
            <Globe className="h-3.5 w-3.5 text-gray-500" />
            <span className="text-xs font-medium">{currentLang.code.toUpperCase()}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`cursor-pointer ${i18n.language === lang.code ? 'bg-primary/10' : ''}`}
              data-testid={`lang-${lang.code}`}
            >
              <span className="mr-2 text-lg">{lang.flag}</span>
              <span className="text-sm">{lang.name}</span>
              {i18n.language === lang.code && (
                <span className="ml-auto text-primary text-xs">✓</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="language-selector">
          <span className="text-lg">{currentLang.flag}</span>
          <span className="hidden sm:inline">{currentLang.name}</span>
          <Globe className="h-4 w-4 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          {t('common.language')}
        </div>
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`cursor-pointer ${i18n.language === lang.code ? 'bg-primary/10' : ''}`}
            data-testid={`lang-${lang.code}`}
          >
            <span className="mr-2 text-lg">{lang.flag}</span>
            <span>{lang.name}</span>
            {i18n.language === lang.code && (
              <span className="ml-auto text-primary">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
